import { isRouteErrorResponse } from "react-router";

/**
 * React Router v7 throws `SingleFetchNoResultError` client-side when a
 * single-fetch response is missing data for a route the client expected.
 * Causes in the wild are transient: navigating while a previous
 * navigation's single-fetch is still in flight or has been aborted,
 * route discovery races, and bfcache restores. The router recovers on
 * the next navigation, so the error is user-invisible noise.
 *
 * The error class is not exported by react-router, so we detect it by
 * the message prefix the framework throws with — see
 * `packages/react-router/lib/dom/ssr/single-fetch.tsx`
 * (`unwrapSingleFetchResult`).
 *
 * Tracking issue: https://github.com/remix-run/react-router/issues/13873
 */
export function isSingleFetchNoResultError(error: unknown): boolean {
	return (
		error instanceof Error &&
		error.message.startsWith("No result found for routeId")
	);
}

/**
 * React Router 7 wraps a `Response` thrown from a loader/action (or returned
 * via `data(...)` with an error status) in an internal `ErrorResponseImpl`
 * shaped `{ data, internal, status, statusText }`. RR rethrows that object
 * to the nearest error boundary as the `useRouteError()` value, and our
 * `<HydratedRouter onError={Sentry.sentryOnError} />` wiring also forwards
 * it to Sentry's react-router integration.
 *
 * Two problems with capturing these in Sentry:
 *
 * 1. `ErrorResponseImpl` is not an `Error` — Sentry can't extract a stack,
 *    so the issue surfaces as a useless `"Object captured as exception with
 *    keys: data, internal, status, statusText"`. See WEB-MAIN-1BV / 1BW.
 *
 * 2. 4xx route error responses are intentional control flow — a 401 from
 *    an unauthenticated loader, a 403 from a permission check, a 404 from
 *    a missing resource. The framework redirects to `/login` or renders the
 *    error boundary; the user is not seeing a bug. 5xx responses *do*
 *    indicate a real backend bug, but those are already captured server-side
 *    by `monitoring.server.tsx`'s `Sentry.init` (which sees the response
 *    before it crosses the network boundary), so the client-side dedupe is
 *    safe.
 *
 * We therefore drop only `< 500` route error responses; 5xx still flow
 * through so we keep visibility on backend regressions that the server
 * Sentry might miss (e.g. errors thrown after the response started
 * streaming).
 */
export function isClientRouteErrorResponse(error: unknown): boolean {
	return isRouteErrorResponse(error) && error.status < 500;
}
